import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Slider,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconEraser, IconMicrophone, IconPhoneOff, IconVideo } from '@tabler/icons-react';
import { BoardEngine } from '@domain/realtime/whiteboard/BoardEngine';
import type { BoardPatch, BoardSnapshot } from '@domain/realtime/types';
import type {
  ActiveRealtimeSession,
  IncomingRealtimeSession,
} from '../hooks/useRealtimeCollaboration';
import { AppIcon } from '@shared/ui/AppIcon';
import { UserAvatar } from '@shared/ui/UserAvatar';

type StreamTileProps = {
  stream: MediaStream | null;
  label: string;
  mode: 'audio' | 'video';
  muted?: boolean;
};

function useStreamAudioLevel(stream: MediaStream | null) {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0 || typeof window === 'undefined') {
      setLevel(0);
      return;
    }

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
      setLevel(0);
      return;
    }

    let cancelled = false;
    let animationFrame = 0;
    const audioContext = new AudioContextCtor();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.72;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (cancelled) {
        return;
      }
      analyser.getByteTimeDomainData(samples);
      let total = 0;
      for (const sample of samples) {
        const centered = (sample - 128) / 128;
        total += centered * centered;
      }
      setLevel(Math.min(1, Math.sqrt(total / samples.length) * 4));
      animationFrame = window.requestAnimationFrame(tick);
    };

    void audioContext.resume().catch(() => undefined);
    tick();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(animationFrame);
      source.disconnect();
      analyser.disconnect();
      void audioContext.close().catch(() => undefined);
      setLevel(0);
    };
  }, [stream]);

  return level;
}

function VoiceActivityMark({ level }: { level: number }) {
  const normalizedLevel = Math.max(0, Math.min(1, level));
  const active = normalizedLevel >= 0.08;
  const heights = [
    7 + normalizedLevel * 10,
    11 + normalizedLevel * 16,
    8 + normalizedLevel * 12,
  ];
  return (
    <Group
      gap={3}
      align="flex-end"
      h={26}
      px={8}
      py={5}
      aria-label={active ? '正在说话' : '未检测到说话'}
      style={{
        borderRadius: 999,
        backgroundColor: 'rgba(0,0,0,0.42)',
        border: `1px solid ${active ? 'rgba(48, 210, 124, 0.92)' : 'rgba(255,255,255,0.18)'}`,
      }}
    >
      {heights.map((height, index) => (
        <Box
          key={index}
          w={4}
          h={height}
          style={{
            borderRadius: 999,
            backgroundColor: active ? '#30D27C' : 'rgba(255,255,255,0.55)',
            transition: 'height 140ms ease, background-color 140ms ease',
          }}
        />
      ))}
    </Group>
  );
}

function StreamTile({ stream, label, mode, muted = false }: StreamTileProps) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioLevel = useStreamAudioLevel(stream);
  const isSpeaking = audioLevel >= 0.08;

  useEffect(() => {
    if (ref.current && ref.current.srcObject !== stream) {
      ref.current.srcObject = stream;
      void ref.current.play().catch(() => undefined);
    }
    if (audioRef.current && audioRef.current.srcObject !== stream) {
      audioRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <Paper
      radius="md"
      withBorder
      aria-label={isSpeaking ? `${label} 正在说话` : label}
      style={{
        overflow: 'hidden',
        background: '#101817',
        borderColor: isSpeaking ? 'rgba(48, 210, 124, 0.92)' : 'rgba(255,255,255,0.16)',
        boxShadow: isSpeaking ? '0 0 0 1px rgba(48,210,124,0.2), 0 18px 40px rgba(48,210,124,0.16)' : undefined,
        minHeight: 180,
        position: 'relative',
        transition: 'border-color 160ms ease, box-shadow 160ms ease',
      }}
    >
      {mode === 'video' && stream ? (
        <video
          ref={ref}
          autoPlay
          playsInline
          muted
          data-realtime-video
          style={{ width: '100%', height: '100%', minHeight: 180, objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Stack align="center" justify="center" h={180}>
          <UserAvatar id={0} name={label} size={54} fallback={label.slice(0, 2)} />
          {mode === 'audio' && (
            <Badge variant="light" color={stream ? 'green' : 'gray'} radius="sm">
              {stream ? '语音已连接' : '等待语音'}
            </Badge>
          )}
        </Stack>
      )}
      {stream && !muted && (
        // Audio rooms and remote video tiles still need a real media sink for playback.
        <audio ref={audioRef} data-realtime-audio autoPlay playsInline />
      )}
      <Badge
        size="sm"
        radius="sm"
        color="dark"
        variant="filled"
        style={{ position: 'absolute', left: 10, bottom: 10, textTransform: 'none' }}
      >
        {label}
      </Badge>
      {stream && (
        <Box pos="absolute" right={10} bottom={10}>
          <VoiceActivityMark level={audioLevel} />
        </Box>
      )}
    </Paper>
  );
}

type WhiteboardSurfaceProps = {
  onPatch: (patch: BoardPatch) => void;
  remotePatch: { patch: BoardPatch; nonce: number } | null;
  remoteSnapshot: { snapshot: BoardSnapshot; nonce: number } | null;
};

function WhiteboardSurface({ onPatch, remotePatch, remoteSnapshot }: WhiteboardSurfaceProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<BoardEngine | null>(null);
  const [color, setColor] = useState('#154f46');
  const [lineWidth, setLineWidth] = useState(5);

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }
    const engine = new BoardEngine(canvasRef.current, {
      color,
      lineWidth,
      onPatch,
    });
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, [onPatch]);

  useEffect(() => {
    engineRef.current?.setStyle({ color, lineWidth });
  }, [color, lineWidth]);

  useEffect(() => {
    if (remotePatch) {
      engineRef.current?.applyPatch(remotePatch.patch);
    }
  }, [remotePatch]);

  useEffect(() => {
    if (remoteSnapshot) {
      engineRef.current?.replaceSnapshot(remoteSnapshot.snapshot);
    }
  }, [remoteSnapshot]);

  return (
    <Stack gap={0}>
      {/* Toolbar */}
      <Group
        justify="space-between"
        align="center"
        px="md"
        py="xs"
        style={{ flexShrink: 0 }}
      >
        <Group gap="sm" align="center">
          <input
            type="color"
            value={color}
            onChange={(event) => setColor(event.currentTarget.value)}
            aria-label="画笔颜色"
            style={{
              width: 32,
              height: 28,
              border: '1px solid var(--locs-border)',
              borderRadius: 6,
              background: 'transparent',
              cursor: 'pointer',
              padding: 2,
            }}
          />
          <Box w={160}>
            <Slider
              value={lineWidth}
              min={1}
              max={18}
              onChange={setLineWidth}
              size="sm"
              color="terracotta"
              label={(value) => `${value}px`}
              styles={{
                root: { paddingBottom: 28 },
                label: {
                  top: 'calc(100% + 6px)',
                  bottom: 'auto',
                  transform: 'translateX(-50%)',
                },
              }}
            />
          </Box>
        </Group>
        <Tooltip label="清空画板">
          <ActionIcon
            variant="light"
            color="red"
            radius="md"
            aria-label="清空画板"
            onClick={() => {
              if (window.confirm('确定要清空画布吗？当前内容将被移除。')) {
                engineRef.current?.clear(true);
              }
            }}
          >
            <IconEraser size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Canvas — full width, fixed height, no scrollbar */}
      <Box
        style={{
          padding: '12px',
        }}
      >
        <Box
          style={{
            width: '100%',
            height: 'min(58vh, 540px)',
            overflow: 'hidden',
            touchAction: 'none',
          }}
        >
          <canvas
            ref={canvasRef}
            width={1200}
            height={900}
            style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair', background: 'var(--locs-panel)' }}
          />
        </Box>
      </Box>
    </Stack>
  );
}

type Props = {
  incomingSession: IncomingRealtimeSession | null;
  activeSession: ActiveRealtimeSession | null;
  localStream: MediaStream | null;
  remoteStreams: Array<{ userId: number; stream: MediaStream }>;
  remoteBoardPatch: { patch: BoardPatch; nonce: number } | null;
  remoteBoardSnapshot: { snapshot: BoardSnapshot; nonce: number } | null;
  currentUserName: string;
  resolveUserName: (userId: number) => string;
  acceptIncoming: () => Promise<void>;
  rejectIncoming: () => void;
  endSession: () => void;
  sendBoardPatch: (patch: BoardPatch) => void;
};

export function RealtimeCollaborationOverlays({
  incomingSession,
  activeSession,
  localStream,
  remoteStreams,
  remoteBoardPatch,
  remoteBoardSnapshot,
  currentUserName,
  resolveUserName,
  acceptIncoming,
  rejectIncoming,
  endSession,
  sendBoardPatch,
}: Props) {
  const isVideo = activeSession?.kind === 'group-video';
  const isAudio = activeSession?.kind === 'group-audio';
  const isMedia = isVideo || isAudio;
  const isWhiteboard = activeSession?.kind === 'direct-whiteboard' || activeSession?.kind === 'group-whiteboard';

  return (
    <>
      <Modal
        opened={!!incomingSession}
        onClose={rejectIncoming}
        centered
        radius="md"
        title={<Text fw={900}>{incomingSession?.kind.includes('whiteboard') ? '画板邀请' : '通话邀请'}</Text>}
      >
        {incomingSession && (
          <Stack gap="md">
            <Group gap="sm">
              <UserAvatar id={incomingSession.fromUserId} name={incomingSession.fromName} size={44} />
              <Stack gap={2}>
                <Text fw={800}>{incomingSession.fromName}</Text>
                <Text size="sm" c="dimmed">
                  {incomingSession.kind.includes('whiteboard') ? '邀请你一起随手画画' : incomingSession.kind.includes('audio') ? '邀请你加入语音通话' : '邀请你加入视频通话'}
                </Text>
              </Stack>
            </Group>
            <Group justify="flex-end">
              <Button variant="default" onClick={rejectIncoming}>拒绝</Button>
              <Button color="terracotta" onClick={() => void acceptIncoming()}>
                加入
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={!!activeSession && isMedia}
        onClose={endSession}
        fullScreen
        radius={0}
        padding="lg"
        title={(
          <Group gap="xs">
            <AppIcon icon={isAudio ? IconMicrophone : IconVideo} size={20} />
            <Text fw={900}>{activeSession?.conversation.title ?? (isAudio ? '语音房间' : '视频房间')}</Text>
            <Badge color="green" variant="light" radius="sm">{remoteStreams.length + 1} 人</Badge>
          </Group>
        )}
      >
        <Stack gap="md" h="calc(100dvh - 110px)">
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 12,
              flex: 1,
              minHeight: 0,
            }}
          >
            <StreamTile stream={localStream} label={`${currentUserName}（我）`} mode={isAudio ? 'audio' : 'video'} muted />
            {remoteStreams.map((entry) => (
              <StreamTile key={entry.userId} stream={entry.stream} label={resolveUserName(entry.userId)} mode={isAudio ? 'audio' : 'video'} />
            ))}
          </Box>
          <Group justify="center">
            <Button
              color="red"
              radius="xl"
              size="md"
              leftSection={<IconPhoneOff size={18} />}
              onClick={endSession}
            >
              结束房间
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!activeSession && isWhiteboard}
        onClose={endSession}
        size="min(1100px, 96vw)"
        radius="md"
        padding={0}
        centered
        title={(
          <Text fw={900}>{activeSession?.conversation.title ?? '共享画板'}</Text>
        )}
        styles={{
          header: {
            padding: '16px 20px',
            flexShrink: 0,
          },
          body: {
            padding: 0,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
          },
          content: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
          },
        }}
      >
        <WhiteboardSurface
          onPatch={sendBoardPatch}
          remotePatch={remoteBoardPatch}
          remoteSnapshot={remoteBoardSnapshot}
        />
      </Modal>
    </>
  );
}
